import { Box, Button, Tab, Tabs } from '@mui/material';
import { DocumentStatus, FilterParams } from '@synergy/models';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import {
    PaloAmount,
    PaloColumn,
    PaloListView,
    PaloRowAction,
    PaloStatusPill,
    usePaloListSort,
} from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { UrlFilter } from '@synergy/components';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as InvoiceActions from '../invoice-overview/store/invoice-actions';
import { ExportInvoicesModal } from '../invoice-overview/components/export-invoices-modal';
import { Invoice } from '../models/invoice';
import { PaloCompleteInvoicesDialog } from './palo-complete-invoices-dialog';
import { PaloContactName } from '../../../common/components/palo/palo-contact-card';
import { PaloDocumentNumberCell } from '../../../common/components/palo/palo-document-number-cell';
import { PaloInvoiceOverviewFilter } from './palo-invoice-overview-filter';
import { PaloNamedFilterChips } from '../../../common/components/palo/palo-named-filter-chips';
import { PaloSkippedInvoicesDialog } from './palo-skipped-invoices-dialog';
import { SkippedInvoiceResponse } from '../invoice-api';
import { fetchAllInvoiceNumberSequences } from '../../configuration/finance/invoice-number-sequence/store/invoice-number-sequence-actions';
import { invoiceNumberSequencesAllSelector } from '../../configuration/finance/invoice-number-sequence/store/invoice-number-sequence-selector';
import { localBlocker } from './invoice-completion';
import { lockedPerson } from '../../../common/utils/locked-partner-notice';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../common/components/palo/use-palo-column-filters';
import { usePaloListFilters } from '../../../common/components/palo/use-palo-list-filters';
import { usePaloListViews } from '../../../common/components/palo/use-palo-list-views';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export const PaloInvoiceOverview = (): ReactElement => {
    // No saved views here: administration has no use for them (commented
    // feedback, 2026-09-04). The column choice stays.
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('invoices');
    const columnFilter = usePaloColumnFilters('invoices');
    // Handed to the chips so that an amount reads as an amount.
    const filterFields = usePaloListFilters('invoices');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloListSort('invoices');
    const [exportOpen, setExportOpen] = useState(false);
    const [completeOpen, setCompleteOpen] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [skipped, setSkipped] = useState<SkippedInvoiceResponse[]>([]);
    const { flat: paginated, requesting } = useAppSelector(state => state.invoices);
    const currency = useAppSelector(state => state.settings.currency);
    const numberSequences = useAppSelector(invoiceNumberSequencesAllSelector);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);
    const activeSequence = searchParams.get(FilterParams.InvoiceNumberSequenceId) ?? 'all';

    useEffect(() => {
        dispatch(InvoiceActions.fetchInvoices(params));
    }, [dispatch, params]);

    useEffect(() => {
        dispatch(fetchAllInvoiceNumberSequences());
    }, [dispatch]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const onSelectSequence = (value: string): void => {
        const next = new URLSearchParams(searchParams);
        if (value === 'all') {
            next.delete(FilterParams.InvoiceNumberSequenceId);
        } else {
            next.set(FilterParams.InvoiceNumberSequenceId, value);
        }
        next.set('page', '1');
        setSearchParams(next);
    };

    const detailHref = (invoice: Invoice): string => `/invoices/${invoice.id}`;

    const openDetail = (invoice: Invoice): void => navigate(detailHref(invoice));

    const [selected, setSelected] = useState<number[]>([]);
    const allOnPageSelected = paginated.items.length > 0 && paginated.items.every(i => selected.includes(i.id));
    const someOnPageSelected = paginated.items.some(i => selected.includes(i.id));

    const handleDeleteSelected = (): void => {
        dispatch(InvoiceActions.deleteInvoices(selected));
        setSelected([]);
    };

    const selectedInvoices = paginated.items.filter(invoice => selected.includes(invoice.id));
    const completableCount = selectedInvoices.filter(invoice => undefined === localBlocker(invoice)).length;

    const handleComplete = async (ids: number[]): Promise<void> => {
        setCompleting(true);

        const result = await dispatch(InvoiceActions.completeInvoices(ids));

        setCompleting(false);
        setCompleteOpen(false);
        setSelected([]);

        // The dialog checks the same rules before sending, so a skip here means
        // the invoice changed under the user or a rule only the server knows
        // said no. Silence would leave them believing the batch was whole.
        if (result && result.skipped.length > 0) {
            setSkipped(result.skipped);
        }
    };

    const columns: PaloColumn<Invoice>[] = [
        {
            key: 'number',
            sortKey: 'number',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            // The note has to be visible from the list, the way it already is on
            // inquiries, orders and demands — otherwise it is only found by
            // opening the invoice (#2184).
            cell: invoice => <PaloDocumentNumberCell number={invoice.number} alert={invoice.receiptAlert} />,
        },
        {
            key: 'status',
            sortKey: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            // No filter on the column itself: the same filter in two places wrote two
            // different keys into the address and answered each other (LST-S9, GF
            // 13.10.2026). The navigation on the left and the filter dialog set it in
            // one key (#2340); the active one stands as a chip over the list.
            cell: invoice => (
                <PaloStatusPill
                    status={invoice.status}
                    label={<FormattedMessage id={getDocumentStatusTranslationId(invoice.status)} />}
                />
            ),
        },
        {
            key: 'type',
            sortKey: 'type',
            optional: true,
            head: <FormattedMessage id="app.table.column.type" />,
            cell: invoice => (
                <FormattedMessage
                    id={
                        invoice.isCredit
                            ? 'app.invoices.detail.overview.isCredit.true'
                            : 'app.invoices.detail.overview.isCredit.false'
                    }
                />
            ),
        },
        {
            key: 'recipient',
            type: 'name',
            sortKey: 'recipientAccount',
            head: <FormattedMessage id="app.table.column.recipient" />,
            cell: invoice => <PaloContactName company={invoice.recipientAccount} kind="customer" />,
        },
        {
            key: 'supplier',
            type: 'name',
            sortKey: 'supplierAccount',
            head: <FormattedMessage id="app.table.column.supplier" />,
            cell: invoice => <PaloContactName company={invoice.supplierAccount} kind="supplier" />,
        },
        {
            key: 'responsibleContact',
            type: 'name',
            optional: true,
            sortKey: 'responsibleContact',
            head: <FormattedMessage id="app.common.responsibleContact" />,
            // Filtered in the dialog only, as in the other lists: the column
            // offered it a second time (GF 30.10.2026, #2340).
            cell: invoice => <PaloContactName person={invoice.responsibleContact} />,
        },
        {
            // The date the list opens in order of, so the order it shows can
            // be read off the rows themselves (LST-S16, #2234).
            key: 'invoiceDate',
            type: 'date',
            sortKey: 'invoiceDate',
            head: <FormattedMessage id="app.table.column.invoiceDate" />,
            cell: invoice =>
                invoice.invoiceDate && (
                    <FormattedDate value={invoice.invoiceDate} year="numeric" month="short" day="2-digit" />
                ),
        },
        {
            key: 'createdAt',
            type: 'date',
            sortKey: 'createdAt',
            head: <FormattedMessage id="app.table.column.createdAt" />,
            cell: invoice => <FormattedDate value={invoice.createdAt} year="numeric" month="short" day="2-digit" />,
        },
        {
            key: 'total',
            type: 'amount',
            sortKey: 'totalPrice',
            currency,
            head: <FormattedMessage id="app.table.column.invoiceTotal" />,
            cell: invoice => (
                <Box component="span" sx={{ color: invoice.totalPrice < 0 ? tokens.color.crit : 'inherit' }}>
                    <PaloAmount value={invoice.totalPrice} currency={invoice.currency} base={currency} />
                </Box>
            ),
        },
    ];

    const rowActions = (invoice: Invoice): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(invoice) },
        {
            label: 'Duplizieren',
            onClick: () =>
                dispatch(
                    InvoiceActions.duplicateInvoice(
                        invoice.id,
                        [
                            invoice.recipientAccount,
                            invoice.supplierAccount,
                            lockedPerson(invoice.recipientContact),
                            lockedPerson(invoice.supplierContact),
                        ],
                        params
                    )
                ),
        },
        { label: 'Stornieren', onClick: () => dispatch(InvoiceActions.cancelInvoice(invoice.id)) },
    ];

    const headerActions = (
        <Button variant="contained" color="primary" onClick={() => navigate('/invoices/new')}>
            <FormattedMessage id="app.invoices.new" />
        </Button>
    );

    const selectionActions = (
        <>
            <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={0 === completableCount}
                onClick={() => setCompleteOpen(true)}
            >
                <FormattedMessage id="app.invoices.completeSelected" values={{ count: completableCount }} />
            </Button>
            <Button variant="outlined" color="error" size="small" onClick={handleDeleteSelected}>
                <FormattedMessage id="app.common.delete" />
            </Button>
        </>
    );

    const tabs = (
        <Tabs
            value={activeSequence}
            onChange={(_, value) => onSelectSequence(value)}
            sx={{ mb: 2, borderBottom: `1px solid ${tokens.color.line}` }}
        >
            <Tab value="all" label="Alle" />
            {numberSequences.map(seq => (
                <Tab
                    key={seq.id}
                    value={String(seq.id)}
                    label={seq.prefix ?? <FormattedMessage id="app.filter.invoiceNumberSequenceStandard" />}
                />
            ))}
        </Tabs>
    );

    return (
        <UrlFilter useLegacyFilter={true}>
            <>
                <PaloListView<Invoice>
                    title={<FormattedMessage id="app.navigation.invoices" />}
                    pageTitle={intl.formatMessage({ id: 'app.navigation.invoices' })}
                    onExport={() => setExportOpen(true)}
                    sort={sort}
                    onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                    filters={
                        <>
                            <PaloInvoiceOverviewFilter />
                            {savedFiltersMenu}
                        </>
                    }
                    filterChips={<PaloNamedFilterChips listKey="invoices" fields={filterFields} currency={currency} />}
                    searchValue={searchParams.get('search') ?? ''}
                    searchPlaceholder="Nummer, Mitglied, Lieferant, Artikel …"
                    onSearchChange={value => {
                        const next = new URLSearchParams(searchParams);
                        if (value) {
                            next.set('search', value);
                        } else {
                            next.delete('search');
                        }
                        next.set('page', '1');
                        setSearchParams(next, { replace: true });
                    }}
                    headerActions={headerActions}
                    selectionActions={selectionActions}
                    tabs={tabs}
                    columns={orderColumns(columns)}
                    columnFilter={columnFilter}
                    onMoveColumn={moveColumn}
                    hiddenColumnKeys={hiddenColumnKeys}
                    shownColumnKeys={shownColumnKeys}
                    onToggleColumn={toggleColumn}
                    items={paginated.items}
                    getRowId={invoice => invoice.id}
                    requesting={requesting}
                    emptyLabel="Keine Rechnungen."
                    rowHref={detailHref}
                    rowActions={rowActions}
                    selection={{
                        selectedIds: selected,
                        allSelected: allOnPageSelected,
                        someSelected: someOnPageSelected,
                        onToggle: id =>
                            setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])),
                        onToggleAll: () => setSelected(allOnPageSelected ? [] : paginated.items.map(i => i.id)),
                    }}
                    pagination={{
                        count: paginated.total,
                        page,
                        limit,
                        onPageChange: newPage => updateParams({ page: String(newPage) }),
                        onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                    }}
                />

                <PaloCompleteInvoicesDialog
                    open={completeOpen}
                    busy={completing}
                    selectedInvoices={selectedInvoices}
                    onClose={() => setCompleteOpen(false)}
                    onConfirm={handleComplete}
                />

                <PaloSkippedInvoicesDialog skipped={skipped} onClose={() => setSkipped([])} />

                {/* Legacy modal: keep it outside the list so its body stays on the ambient theme. */}
                <ExportInvoicesModal isVisible={exportOpen} onClose={() => setExportOpen(false)} />
            </>
        </UrlFilter>
    );
};
